import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { type ITenantFooter } from '@/common/types/tenant-config.types';
import { Divider } from 'antd';
import Button from '@/common/components/button';
import BaleIcon from '~/public/assets/images/svg/socialmedias/Bale.svg';
import EitaaIcon from '~/public/assets/images/svg/socialmedias/Eitaa.svg';
import InstagramIcon from '~/public/assets/images/svg/socialmedias/Instagram.svg';
import RubikaIcon from '~/public/assets/images/svg/socialmedias/Rubika.svg';
import TelegramIcon from '~/public/assets/images/svg/socialmedias/Telegram.svg';
import WhatsappIcon from '~/public/assets/images/svg/socialmedias/Whatsapp.svg';
import FooterNavLink from './footer-nav-link.component';

interface MobileFooterProps {
  footerData: ITenantFooter;
  isContactUsEnabled: boolean;
}

function MobileAddressTelSection({
  address,
  tels,
  mobiles,
}: {
  address: string;
  tels: string[];
  mobiles: string[];
}) {
  if (!address?.length && !tels?.length && !mobiles.length) return null;
  return (
    <div>
      <h4 className="pb-3 text-base font-semibold text-black-default">
        نشانی ما
      </h4>
      {address?.length ? (
        <p className="pb-2 text-sm font-medium text-black-default">{address}</p>
      ) : null}
      {tels?.length ? (
        <div className="flex items-center justify-between pb-6">
          <span className="text-sm font-medium text-black-400">
            شماره ثابت:
          </span>

          <div className="flex items-center gap-2">
            <Button
              type="link"
              link={`tel:${tels[0]}`}
              className="text-sm font-medium"
            >
              {tels[0]}
            </Button>
            {tels.length > 1 && (
              <>
                <span className="text-blue-default">-</span>
                <Button
                  type="link"
                  link={`tel:${tels[1]}`}
                  className="text-sm font-medium"
                >
                  {tels[1]}
                </Button>
              </>
            )}
          </div>
        </div>
      ) : null}
      {mobiles?.length ? (
        <div className="flex items-center justify-between pb-6">
          <span className="text-sm font-medium text-black-400">
            شماره همراه:
          </span>

          <div className="flex items-center gap-2">
            <Button
              type="link"
              link={`tel:${mobiles[0]}`}
              className="text-sm font-medium"
            >
              {mobiles[0]}
            </Button>
            {mobiles.length > 1 && (
              <>
                <span className="text-blue-default">-</span>
                <Button
                  type="link"
                  link={`tel:${mobiles[1]}`}
                  className="text-sm font-medium"
                >
                  {mobiles[1]}
                </Button>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MobileLinkGroupsSection({
  LinkGroup1,
  LinkGroup2,
}: {
  LinkGroup1: Array<{ title: string; link: string }>;
  LinkGroup2: Array<{ title: string; link: string }>;
}) {
  if (!LinkGroup1?.length && !LinkGroup2?.length) return null;
  return (
    <div className="grid grid-cols-2 pb-6">
      {LinkGroup1?.length ? (
        <FooterNavLink title="درباره فروشگاه" list={LinkGroup1} />
      ) : null}
      {LinkGroup2?.length ? (
        <FooterNavLink title="خدمات مشتریان" list={LinkGroup2} />
      ) : null}
    </div>
  );
}

function MobileSocialMediasSection({
  SocialMedias,
}: {
  SocialMedias: ITenantFooter['socialMedias'];
}) {
  if (!SocialMedias || !Object.values(SocialMedias).some((link) => !!link))
    return null;
  const imageMapper = (socialMedia: string) => {
    switch (socialMedia) {
      case 'telegram':
        return <TelegramIcon />;
      case 'whatsapp':
        return <WhatsappIcon />;
      case 'bale':
        return <BaleIcon />;
      case 'eitaa':
        return <EitaaIcon />;
      case 'instagram':
        return <InstagramIcon />;
      case 'rubika':
        return <RubikaIcon />;
      default:
        return null;
    }
  };
  return (
    <div className="w-full">
      <div className="flex w-full items-center justify-center gap-3">
        {Object.entries(SocialMedias)
          .filter(([, link]) => !!link)
          .map(([socialMedia, link]) => (
            <Link
              href={link ?? ''}
              key={socialMedia}
              target="_blank"
              className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-silver-200"
            >
              <div className="w-5 h-5">{imageMapper(socialMedia)}</div>
            </Link>
          ))}
      </div>
    </div>
  );
}

function MobileNemadsSection({ nemads }: { nemads: ITenantFooter['nemads'] }) {
  if (!nemads || !Object.keys(nemads).length) return null;
  return (
    <div className="flex items-start justify-center gap-4 pb-6">
      {Object.entries(nemads).map(([nemad, script]) => (
        <div
          className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-30 [&_img]:h-[42px] [&_img]:w-8 [&_img]:object-contain"
          key={nemad}
          dangerouslySetInnerHTML={{ __html: script }}
        ></div>
      ))}
    </div>
  );
}

function MobileFooter({ footerData, isContactUsEnabled }: MobileFooterProps) {
  const {
    address = '',
    description = '',
    pagesStatus,
    logoUrl,
    socialMedias,
    tels = [],
    mobiles = [],
    title = '',
    nemads,
  } = footerData;
  const linkGroup1: Array<{ link: string; title: string }> = [];
  const linkGroup2: Array<{ link: string; title: string }> = [];
  const renderNavGroups = () => {
    if (pagesStatus.contentContactUs) {
      linkGroup1.push({
        link: '/contact-us',
        title: 'تماس با ما',
      });
    }
    if (pagesStatus.contentAboutUs) {
      linkGroup1.push({
        link: '/about-us',
        title: 'درباره ما',
      });
    }
    if (pagesStatus.contentPrivacyAndPolicy) {
      linkGroup1.push({
        link: '/privacy-policies',
        title: 'قوانین و مقررات',
      });
    }
    if (pagesStatus.contentFAQ) {
      linkGroup2.push({
        link: '/faq',
        title: 'سوالات متداول',
      });
    }
    if (pagesStatus.contentDelivery) {
      linkGroup2.push({
        link: '/delivery',
        title: 'پیگیری سفارش',
      });
    }
    if (pagesStatus.contentReturn) {
      linkGroup2.push({
        link: '/return',
        title: 'رویه‌های بازگرداندن کالا',
      });
    }
  };
  renderNavGroups();
  return (
    <footer className="rounded-t-3xl bg-white p-7">
      <div className="flex items-center gap-1 pb-4">
        {logoUrl && (
          <Image src={logoUrl} alt="Vendor Logo" width={40} height={40} />
        )}
        <h3 className="px-2 py-1 text-base font-semibold text-black-default">
          {title}
        </h3>
      </div>
      <p className="pb-6 text-right text-sm font-medium text-black-400">
        {description}
      </p>
      {isContactUsEnabled && (
        <MobileAddressTelSection
          address={address}
          tels={tels}
          mobiles={mobiles}
        />
      )}
      <MobileLinkGroupsSection
        LinkGroup1={linkGroup1}
        LinkGroup2={linkGroup2}
      />
      <MobileSocialMediasSection SocialMedias={socialMedias} />
      <Divider className="my-8" />
      <MobileNemadsSection nemads={nemads} />
      <div className="flex flex-col items-center">
        <p className="flex items-center gap-1 pb-3 text-center text-xs font-medium text-black-400">
          <span>©</span> تمامی حقوق متعلق به {title} می‌باشد.
        </p>
        <span className="flex items-center gap-1 text-xs font-medium text-black-400">
          قدرت گرفته از{' '}
          <Image
            src={'/assets/images/svg/logo-powered.svg'}
            alt="powered by witro"
            width={16}
            height={16}
          />{' '}
          <Link href={'https://witro.ir'}>ویترو</Link>
        </span>
      </div>
    </footer>
  );
}

export default MobileFooter;
